package com.dignicate.web.data

import com.dignicate.web.domain.AboutContent
import com.dignicate.web.domain.CompanyContent
import com.dignicate.web.domain.ContentRepository
import com.dignicate.web.domain.FooterNav
import com.dignicate.web.domain.HeroContent
import com.dignicate.web.domain.Language
import com.dignicate.web.domain.LinkItem
import com.dignicate.web.domain.MainPageContent
import com.dignicate.web.domain.NavigationLabels
import com.dignicate.web.domain.PrivacyPolicyContent
import com.dignicate.web.domain.PrivacyPolicySection
import com.dignicate.web.domain.ProductInfo
import com.dignicate.web.domain.TechnologyArea
import com.dignicate.web.domain.TechnologyCategory
import com.dignicate.web.domain.ValueStatement
import com.dignicate.web.data.content.AboutContentDto
import com.dignicate.web.data.content.HeroContentDto
import com.dignicate.web.data.content.LinkItemDto
import com.dignicate.web.data.content.PrivacyPolicyContentDto
import com.dignicate.web.data.content.PrivacyPolicySectionDto
import com.dignicate.web.data.content.ProductInfoDto
import com.dignicate.web.data.content.TechnologyAreaDto
import com.dignicate.web.data.content.TechnologyCategoryDto
import com.dignicate.web.data.content.ValueStatementDto
import com.dignicate.web.data.markdown.model.MarkdownBlock
import com.dignicate.web.data.markdown.model.ParsedMarkdownDocument
import com.dignicate.web.data.markdown.model.UnorderedListItem
import com.dignicate.web.data.markdown.parser.SimpleMarkdownParser
import com.dignicate.web.domain.ContentBlock
import com.dignicate.web.domain.DocumentContent
import com.dignicate.web.domain.ListItem
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.flow

/**
 * コンテンツリポジトリの実装
 */
class ContentRepositoryImpl : ContentRepository {
    private val staticContentSource = StaticSiteContentSource()

    override fun toString(): String = "ContentRepositoryImpl"

    override fun getMainPageContent(lang: Language): Flow<Result<MainPageContent>> = flow {
        console.log("ContentRepositoryImpl.getMainPageContent() called with lang: ${lang.code}")

        try {
            val hero = staticContentSource.hero()
            val about = staticContentSource.about()
            val techs = staticContentSource.technologies()
            val products = staticContentSource.products()
            val values = staticContentSource.values()

            // Map to existing domain.MainPageContent structure (string-based)
            val mappedHero = HeroContentDto(
                title = hero.title,
                subtitle = hero.subtitle
            )

            val mappedAbout = AboutContentDto(
                body = about.body,
            )

            val mappedTechs = techs.map { t: TechnologyAreaDto ->
                TechnologyAreaDto(
                    id = t.id,
                    category = when (t.category) {
                        TechnologyCategoryDto.MOBILE -> TechnologyCategoryDto.MOBILE
                        TechnologyCategoryDto.BACKEND -> TechnologyCategoryDto.BACKEND
                        TechnologyCategoryDto.PROCESS -> TechnologyCategoryDto.PROCESS
                    },
                    title = t.title,
                    description = t.description,
                )
            }

            val mappedProducts = products.map { p: ProductInfoDto ->
                ProductInfoDto(
                    id = p.id,
                    title = p.title,
                    description = p.description,
                    androidLink = p.androidLink,
                    iosLink = p.iosLink
                )
            }

            val mappedValues = values.map { v: ValueStatementDto ->
                ValueStatementDto(
                    id = v.id,
                    title = v.title,
                    body = v.body
                )
            }

            val mainPage = MainPageContent(
                pageId = "main",
                hero = mappedHero.toDomain(lang),
                about = mappedAbout.toDomain(lang),
                technologies = mappedTechs.map { it.toDomain(lang) },
                products = mappedProducts.map { it.toDomain(lang) },
                values = mappedValues.map { it.toDomain(lang) },

                // Section headings / intro texts
                technologiesTitle = StaticSiteContentSource.servicesTitle(
                    lang
                ),
                technologiesDescription = StaticSiteContentSource.servicesDescription(
                    lang
                ),
                productsTitle = StaticSiteContentSource.productsTitle(
                    lang
                ),
                productsDescription = StaticSiteContentSource.productsDescription(
                    lang
                ),
                valuesTitle = StaticSiteContentSource.valuesTitle(
                    lang
                ),
                valuesDescription = StaticSiteContentSource.Companion.valuesDescription(
                    lang
                )
            )

            emit(Result.success(mainPage))
        } catch (e: Throwable) {
            console.error("ContentRepositoryImpl - Error:", e.message)
            emit(Result.failure(e))
        }
    }

    override fun getNavigationLabels(lang: Language): Flow<Result<NavigationLabels>> = flow {
        console.log("ContentRepositoryImpl.getNavigationLabels() called with lang: ${lang.code}")
        try {
            // Use StaticSiteContentSource companion helpers for labels when available.
            val aboutLabel = StaticSiteContentSource.aboutTitle(lang)
            val servicesLabel = StaticSiteContentSource.servicesTitle(lang)
            val productsLabel = StaticSiteContentSource.productsTitle(lang)
            val valuesLabel = StaticSiteContentSource.valuesTitle(lang)

            val labels = NavigationLabels(
                about = aboutLabel,
                services = servicesLabel,
                products = productsLabel,
                values = valuesLabel,

                languageLabel = StaticSiteContentSource.Companion.languageLabel(
                    lang
                ),
                languageOptionSystem = StaticSiteContentSource.Companion.languageOptionSystem(
                    lang
                ),
                languageOptionJa = StaticSiteContentSource.Companion.languageOptionJa(
                    lang
                ),
                languageOptionEn = StaticSiteContentSource.Companion.languageOptionEn(
                    lang
                ),

                themeLabel = StaticSiteContentSource.Companion.themeLabel(
                    lang
                ),
                themeOptionSystem = StaticSiteContentSource.Companion.themeOptionSystem(
                    lang
                ),
                themeOptionLight = StaticSiteContentSource.Companion.themeOptionLight(
                    lang
                ),
                themeOptionDark = StaticSiteContentSource.Companion.themeOptionDark(
                    lang
                )
            )

            emit(Result.success(labels))
        } catch (e: Throwable) {
            console.error("ContentRepositoryImpl.getNavigationLabels - Error:", e.toString())
            emit(Result.failure(e))
        }
    }

    // New getCompanyContent implementation
    override fun getCompanyContent(lang: Language): Flow<Result<CompanyContent>> = flow {
        console.log("ContentRepositoryImpl.getCompanyContent() called with lang: ${lang.code}")
        try {
            val src = staticContentSource.company()
            val mappedItems = src.items.map { item ->
                CompanyContent.Item(
                    label = item.label.resolve(lang),
                    sublabel = item.sublabel?.resolve(lang),
                    value = item.value.resolve(lang)
                )
            }
            val mapped = CompanyContent(
                companyName = src.companyName.resolve(lang),
                items = mappedItems
            )
            emit(Result.success(mapped))
        } catch (e: Throwable) {
            console.error("ContentRepositoryImpl.getCompanyContent - Error:", e.toString())
            emit(Result.failure(e))
        }
    }

    // Add privacy policy retrieval
    override fun getPrivacyPolicy(): Flow<Result<DocumentContent>> = flow {
        console.log("ContentRepositoryImpl.getPrivacyPolicy() called")
        try {
            val policy = staticContentSource.getPrivacyPolicyMarkdown()
            val parsed = SimpleMarkdownParser.parse("privacy", Language.En, policy)
            emit(Result.success(parsed.toDomain()))
        } catch (e: Throwable) {
            console.error("ContentRepositoryImpl.getPrivacyPolicy - Error:", e.toString())
            emit(Result.failure(e))
        }
    }

    // Add terms retrieval
    override fun getTerms(): Flow<Result<DocumentContent>> = flow {
        console.log("ContentRepositoryImpl.getTerms() called")
        try {
            val terms = staticContentSource.getTermsMarkdown()
            val parsed = SimpleMarkdownParser.parse("terms", Language.En, terms)
            emit(Result.success(parsed.toDomain()))
        } catch (e: Throwable) {
            console.error("ContentRepositoryImpl.getTerms - Error:", e.toString())
            emit(Result.failure(e))
        }
    }

    override fun getFooterNav(): Flow<Result<FooterNav>> = flow {
        try {
            val footerNav = StaticFooterNavRepository().getFooterNav()
            emit(Result.success(footerNav))
        } catch (e: Throwable) {
            emit(Result.failure(e))
        }
    }
}

private fun HeroContentDto.toDomain(lang: Language): HeroContent {
    return HeroContent(
        title = title.resolve(lang),
        subtitle = subtitle.resolve(lang),
    )
}

private fun AboutContentDto.toDomain(lang: Language): AboutContent {
    return AboutContent(
        description = body.resolve(lang),
    )
}

private fun TechnologyAreaDto.toDomain(lang: Language): TechnologyArea {
    return TechnologyArea(
        id = id,
        category = when (category) {
            TechnologyCategoryDto.MOBILE -> TechnologyCategory.MOBILE
            TechnologyCategoryDto.BACKEND -> TechnologyCategory.BACKEND
            TechnologyCategoryDto.PROCESS -> TechnologyCategory.PROCESS
        },
        title = title.resolve(lang),
        description = description.resolve(lang),
    )
}

private fun ProductInfoDto.toDomain(lang: Language): ProductInfo {
    return ProductInfo(
        id = id,
        title = title.resolve(lang),
        description = description.resolve(lang),
        androidLink = androidLink,
        iosLink = iosLink
    )
}

private fun ValueStatementDto.toDomain(lang: Language): ValueStatement {
    return ValueStatement(
        id = id,
        title = title.resolve(lang),
        body = body.resolve(lang),
    )
}

private fun PrivacyPolicyContentDto.toDomain(): PrivacyPolicyContent {
    return PrivacyPolicyContent(
        title = title,
        introParagraphs = introParagraphs,
        sections = sections.map { it.toDomain() },
        effectiveDate = effectiveDate,
        contactEmail = contactEmail,
        credits = credits,
    )
}

private fun ParsedMarkdownDocument.toDomain(): DocumentContent {
    return DocumentContent(
        title = this.title ?: "",
        metadata = this.metadata,
        blocks = this.blocks.map { it.toDomain() }
    )
}

private fun MarkdownBlock.toDomain(): ContentBlock {
    return when (this) {
        is MarkdownBlock.Heading -> ContentBlock.Heading(this.level, this.text)
        is MarkdownBlock.Paragraph -> ContentBlock.Paragraph(this.text)
        is MarkdownBlock.UnorderedList -> ContentBlock.UnorderedList(this.items.map { it.toDomain() })
        is MarkdownBlock.CodeBlock -> ContentBlock.CodeBlock(this.language, this.code)
    }
}

private fun UnorderedListItem.toDomain(): ListItem {
    return when (this) {
        is UnorderedListItem.Text -> ListItem.Text(this.text)
        is UnorderedListItem.Link -> ListItem.Link(this.text, this.url)
    }
}

private fun PrivacyPolicySectionDto.toDomain(): PrivacyPolicySection {
    return PrivacyPolicySection(
        heading = heading,
        paragraphs = paragraphs,
        bullets = bullets,
        links = links.map { it.toDomain() },
    )
}

private fun LinkItemDto.toDomain(): LinkItem {
    return LinkItem(
        label = label,
        url = url,
    )
}
